// SPDX-FileCopyrightText: 2026 Mads Ringblom
//
// SPDX-License-Identifier: GPL-3.0-or-later

import { api } from '../../../api/client';
import type { ProjectSummary, SurveySummary } from '../../../api/types';
import { useCaseStore } from '../../../app/bygning/CaseDataContext';
import { useCanEdit } from '../../../app/CaseRoleContext';
import { useAsync } from '../../../app/useAsync';
import { measureLines } from '../../../bygning/measure';
import { TextLink } from '../../TextLink';
import styles from './ModelSettings.module.css';

const count = (n: number) => n.toLocaleString('da-DK');

/**
 * Scanning: the facts about the scan behind the model — billeder, panoramaer,
 * instanser and dækning (the share of instance-cloud points with a label,
 * `SurveySummary.classified_share`). `instances` is what the Ground folded
 * from the loaded model; the project summary's component count stands in
 * before it has. Then "Mål fra modellen" (`POST /survey/measure`): the
 * parts' quantities from the typed model's walls, openings and slabs, with
 * the last report's lines — offered when the server has the build-up
 * library (`GET /buildups`, the same ReUseX schema v37).
 */
export function ScanSection({ survey, instances }: { survey: SurveySummary | null; instances: number }) {
  const read = useAsync<ProjectSummary>((signal) => api.projectSummary(signal), []);
  const p = read.data;
  const share = survey?.classified_share;
  const instanceCount = instances > 0 ? instances : (p?.components.total_count ?? null);

  return (
    <dl className={styles.facts}>
      <div className={styles.fact}>
        <dt>Billeder</dt>
        <dd>{p ? count(p.sensor_frames.total_count) : '–'}</dd>
      </div>
      <div className={styles.fact}>
        <dt>Panoramaer</dt>
        <dd>{p ? count(p.panoramic_images.total_count) : '–'}</dd>
      </div>
      <div className={styles.fact}>
        <dt>Instanser fundet</dt>
        <dd>{instanceCount === null ? '–' : count(instanceCount)}</dd>
      </div>
      <div className={styles.fact}>
        <dt>Dækning</dt>
        <dd>{share === null || share === undefined ? '–' : `${Math.round(share * 100)} %`}</dd>
      </div>
    </dl>
  );
}

/** "Mål fra modellen" and what the last run measured. */
export function MeasureSection() {
  const { data, actions } = useCaseStore();
  const canEdit = useCanEdit();
  if (data.buildups === null) {
    return <p className={styles.note}>Mængder og vægt fra modellen kræver en nyere ruxd — her regnes tons ud fra opbygningen.</p>;
  }
  const lines = data.measureReport ? measureLines(data.measureReport) : null;
  return (
    <div className={styles.measure}>
      <div className={styles.row}>
        <span className={styles.note}>Vægge, åbninger og dæk giver delenes mængder; serveren regner tonsene.</span>
        {canEdit && (
          <TextLink size="sm" nowrap disabled={data.busy} onClick={() => void actions.measureSurvey()}>
            Mål fra modellen
          </TextLink>
        )}
      </div>
      {lines && (
        <ul className={styles.measureReport} aria-label="Seneste måling">
          {lines.length === 0 ? <li>Intet at måle endnu</li> : lines.map((l) => <li key={l}>{l}</li>)}
        </ul>
      )}
    </div>
  );
}
